import React from "react";
import { graphql, useFragment } from "react-relay";

import { faPencil } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import * as Sentry from "@sentry/browser";

import { ApplyTemplateModal } from "~/noteTemplate/ApplyModal";
import { Copy, Edit, useNoteTemplateModal } from "~/noteTemplate/modal";
import { setStreamTemplate } from "~/stream/StreamSettings";
import { Button, toast, useModals } from "~/ui";

import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "./MenuItemTooltip";

import type { EditTemplateMenuItem_note$key } from "~/__generated__/EditTemplateMenuItem_note.graphql";

const noteFragment = graphql`
	fragment EditTemplateMenuItem_note on Note {
		id
		version
		isLocked
		stream {
			id
			template {
				id
				viewerCanEdit
				visibility
				title
				summary
				proseDoc
				schemaVersion
				tags
			}
		}
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
	}
`;

interface EditTemplateMenuItemProps {
	note: EditTemplateMenuItem_note$key;
}

export default function EditTemplateMenuItem({ note: noteKey }: EditTemplateMenuItemProps) {
	const note = useFragment(noteFragment, noteKey);
	const streamTemplate = note.stream.template;

	const modals = useModals();
	const openTemplateModal = useNoteTemplateModal();

	const setTemplate = React.useCallback(
		(createdId: string) => async () => {
			const result = await setStreamTemplate(note.stream.id, createdId);

			const newTemplate = result.noteStreamSetTemplateSetting?.stream.template;
			if (!newTemplate) {
				toast.error("Could not set as default template. Try browsing for the template instead.");
			} else {
				toast.success(`Stream template has been set to “${newTemplate.title}”.`, {
					title: "Stream template set",
				});
			}
		},
		[note.stream.id],
	);

	const editTemplate = React.useCallback(async () => {
		if (!streamTemplate) return;

		let templateId = streamTemplate.id;

		if (streamTemplate.viewerCanEdit) {
			const didEdit = await modals.open<boolean>(({ close }) => <Edit close={close} templateId={streamTemplate.id} />);

			if (!didEdit) return;

			if (note.isLocked) {
				toast.success("Template has been updated.");
				return;
			}
		} else {
			const createdId = await modals.open<string>(({ close }) => <Copy close={close} templateId={streamTemplate.id} />);
			if (createdId === null) return;
			templateId = createdId;

			toast.success("Use this template for new notes created in this series?", {
				title: "Default series template",
				autoClose: 20000,
				closeButton: true,
				actions: (
					<Button onClick={setTemplate(createdId)} variant="secondary">
						Set template
					</Button>
				),
			});

			if (note.isLocked) return;
		}

		openTemplateModal({
			mode: "preview",
			actionText: "Apply",
			defaultPreviewing: templateId,
			defaultSelected: templateId,
			hideBackButton: true,
			beforeSubmit: async templateInPreview => {
				if (templateId !== templateInPreview) {
					Sentry.captureMessage("Template changed while previewing, something is wrong", "warning");
				}

				const shouldApplyTemplate = await modals.open<boolean>(({ close }) => (
					<ApplyTemplateModal
						noteId={note.id}
						noteVersion={note.version}
						templateId={templateInPreview}
						close={close}
					/>
				));

				return shouldApplyTemplate ?? false;
			},
		});
	}, [modals, streamTemplate, openTemplateModal, note.id, note.version, note.isLocked, setTemplate]);

	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	return (
		<MenuItemToBeDisabledForExternalWorkspaces
			icon={faPencil}
			disabled={!streamTemplate}
			onSelect={editTemplate}
			{...tooltipPropsForExternalWorkspaces}
		>
			<MenuItemTooltip disabled={Boolean(streamTemplate)} content="This series is not using a template.">
				<span>Edit note template…</span>
			</MenuItemTooltip>
		</MenuItemToBeDisabledForExternalWorkspaces>
	);
}
